import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { KEY_RECOVERY_LAST_RECOVERY_TIME, KEY_RECOVERY_LOG, KEY_RECOVERY_TEST } from '@app/app-components/recovery-screen/constants';
import { RecoveryManagerService } from '@app/app-components/recovery-screen/services/recovery-manager.service';
import { Subject, fromEvent, lastValueFrom, timer } from 'rxjs';
import { defaultIfEmpty, takeUntil, tap } from 'rxjs/operators';
import {
  AppStateService,
  CACHE_INIT_KEY,
  KEY_APP_STATE,
  KEY_CREDENTIAIS,
  TOKEN_KEYS,
  TokenService
} from 'viggo-sistemas-core-web';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class HealthMonitorService {
  private destroying$ = new Subject<void>();
  private requestTimeoutMs = 60000; // 1 minuto para timeout
  private healthCheckIntervalMs = 30000; // Verificar a cada 30 segundos
  private recoveryAttempted = false;
  alreadyDid = false;

  // Valores padrão para restaurar caso as chaves estejam ausentes ou corrompidas
  private defaultLocalStorageValues: Record<string, string> = {
    'app-config': JSON.stringify({ theme: 'light', fontSize: 'medium' }),
    'user-preferences': JSON.stringify({
      notifications: true,
      language: 'pt-BR'
    })
    // Adicione outros valores padrão conforme necessário
  };

  constructor(
    protected appState: AppStateService,
    protected tokenService: TokenService,
    private recoveryManager: RecoveryManagerService,
    private router: Router,
    private cacheService: CacheService
  ) { }

  /**
   * Inicia o monitoramento da saúde da aplicação
   */
  startMonitoring(): void {
    console.log('Iniciando monitoramento de saúde da aplicação');

    // Monitorar requisições de rede
    this.monitorNetworkRequests();

    // Verificações periódicas
    this.scheduleHealthChecks();

    // Monitorar eventos de erro não tratados
    this.monitorUnhandledErrors();

    // Monitorar integridade do localStorage
    this.monitorLocalStorageIntegrity();
  }

  /**
   * Monitora a integridade do localStorage
   */
  private monitorLocalStorageIntegrity(): void {
    this.cacheService.localStorageIntegrity$
      .pipe(takeUntil(this.destroying$))
      .subscribe(isIntegrityOk => {
        if (!isIntegrityOk) {
          // Obter detalhes do problema
          const issue = this.cacheService.getLastIntegrityIssue();

          if (issue) {
            // console.warn(
            //   'Problemas de integridade no localStorage detectados:',
            //   issue.keys
            // );

            // Determinar tempo desde a última recuperação
            const lastRecoveryTime = parseInt(
              localStorage.getItem(KEY_RECOVERY_LAST_RECOVERY_TIME) || '0',
              10
            );
            const now = Date.now();
            const timeSinceLastRecovery = now - lastRecoveryTime;

            // Só iniciar recuperação se a última foi há mais de 2 minutos
            // Isso evita ciclos de recuperação em caso de problemas persistentes
            if (timeSinceLastRecovery > 2 * 60 * 1000) {
              const reason = `Chaves críticas do localStorage comprometidas: ${issue.keys.join(
                ', '
              )}`;

              if (this.recoveryManager.shouldInitiateRecovery(reason)) {
                console.log(
                  'Iniciando recuperação devido a problemas no localStorage'
                );
                this.attemptRecovery(reason);
              }
            } else {
              console.log(
                'Ignorando problema no localStorage devido a recuperação recente'
              );
            }
          }
        }
      });
  }

  /**
   * Para o monitoramento
   */
  stopMonitoring(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  /**
   * Verifica periodicamente se a aplicação está respondendo
   */
  private scheduleHealthChecks(): void {
    timer(this.healthCheckIntervalMs, this.healthCheckIntervalMs)
      .pipe(
        takeUntil(this.destroying$),
        tap(() => this.performHealthCheck())
      )
      .subscribe();
  }

  /**
   * Realiza verificação de saúde da aplicação
   */
  private performHealthCheck(): void {
    if (this.appState.wasCacheInitializedRecently(10000) && localStorage.getItem(CACHE_INIT_KEY)) { // 10 segundos
      return;
    }
    if (
      this.appState && localStorage.getItem(KEY_APP_STATE) &&
      (!this.appState.isAppStable() || !this.appState.currentState)
    ) {
      return;
    }

    // const testCacheOperation = async () => {
    //   try {
    //     // Tenta uma operação de cache simples
    //     const testCache = await caches.open('health-check-test');
    //     await testCache.put('/health-check', new Response('OK'));
    //     const response = await testCache.match('/health-check');
    //     await testCache.delete('/health-check');

    //     return response !== undefined;
    //   } catch (error) {
    //     console.error('Erro na verificação de saúde do cache:', error);
    //     return false;
    //   }
    // };

    // Verifica localStorage
    const testLocalStorage = () => {
      try {
        // Teste básico de funcionamento
        localStorage.setItem('health-check', 'OK');
        const basicCheck = localStorage.getItem('health-check') === 'OK';
        localStorage.removeItem('health-check');

        if (!basicCheck) {
          return false;
        }

        if (!this.tokenService.isLoggedIn()) {
          return false;
        }

        // Verificar existência e validade das chaves essenciais
        const missingOrInvalidKeys = this.checkEssentialLocalStorageKeys();

        // Se houver chaves ausentes ou inválidas, tenta corrigir
        if (missingOrInvalidKeys.length > 0) {
          // console.warn(
          //   'Chaves essenciais ausentes ou inválidas no localStorage:',
          //   missingOrInvalidKeys
          // );
          this.restoreEssentialLocalStorageKeys(missingOrInvalidKeys);
          return false;
        }

        return true;
      } catch (error) {
        console.error('Erro na verificação de localStorage:', error);
        return false;
      }
    };

    // Verifica sessionStorage
    const testSessionStorage = () => {
      try {
        sessionStorage.setItem('health-check', 'OK');
        const result = sessionStorage.getItem('health-check') === 'OK';
        sessionStorage.removeItem('health-check');
        return result;
      } catch (error) {
        console.error('Erro na verificação de sessionStorage:', error);
        return false;
      }
    };

    // Executa os testes
    Promise.all([
      this.cacheService.checkCacheHealth(),
      Promise.resolve(testLocalStorage()),
      Promise.resolve(testSessionStorage())
    ]).then(results => {
      const [cacheOk, localStorageOk, sessionStorageOk] = results;

      if (!cacheOk || !localStorageOk || !sessionStorageOk) {
        // console.warn('Verificação de saúde falhou:', {
        //   cache: cacheOk,
        //   localStorage: localStorageOk,
        //   sessionStorage: sessionStorageOk
        // });

        // Verificar se deve tentar recuperação
        const reason = this.determineRecoveryReason();
        if (this.recoveryManager.shouldInitiateRecovery(reason)) {
          this.attemptRecovery();
        } else {
          console.log('Ignorando tentativa de recuperação devido a restrições');
        }
      }
    });
  }

  /**
   * Monitora erros não tratados
   */
  private monitorUnhandledErrors(): void {
    fromEvent(window, 'error')
      .pipe(
        takeUntil(this.destroying$),
        tap((event: ErrorEvent) => {
          if (this.isCacheRelatedError(event.error)) {
            this.attemptRecovery();
          }
        })
      )
      .subscribe();

    fromEvent(window, 'unhandledrejection')
      .pipe(
        takeUntil(this.destroying$),
        tap((event: PromiseRejectionEvent) => {
          if (this.isCacheRelatedError(event.reason)) {
            this.attemptRecovery();
          }
        })
      )
      .subscribe();
  }

  /**
   * Verifica se o erro está relacionado ao cache
   */
  private isCacheRelatedError(error: any): boolean {
    if (!error) return false;

    const errorString = error.toString().toLowerCase();
    const errorMessage = error.message ? error.message.toLowerCase() : '';

    // Palavras-chave comuns em erros relacionados a cache
    const cacheKeywords = [
      'cache',
      'storage',
      'quota',
      'exceeded',
      'indexed',
      'db',
      'access',
      'idb',
      'storage full',
      'quota_exceeded',
      'quota exceeded'
    ];

    return cacheKeywords.some(
      keyword => errorString.includes(keyword) || errorMessage.includes(keyword)
    );
  }
  /**
   * Verifica se todas as chaves essenciais do localStorage estão presentes e válidas
   * @returns Lista de chaves ausentes ou inválidas
   */
  private checkEssentialLocalStorageKeys(): string[] {
    const missingOrInvalidKeys: string[] = [];
    const criticalKeys = this.cacheService.getCriticalLocalStorageKeys();

    for (const key of criticalKeys) {
      try {
        const value = localStorage.getItem(key);

        // Verificar se a chave existe
        if (value === null || value.length === 0) {
          missingOrInvalidKeys.push(key);
          continue;
        }

        // Para chaves que devem conter JSON válido
        if (
          key.includes('config') ||
          key.includes('preferences') ||
          key.endsWith('json')
        ) {
          try {
            JSON.parse(value);
          } catch (e) {
            // JSON inválido
            missingOrInvalidKeys.push(key);
          }
        }

        // Verificações específicas por tipo de chave
        if (key === 'user' && (!value || value.length < 10)) {
          missingOrInvalidKeys.push(key);
        }

        // Adicione mais verificações específicas conforme necessário
      } catch (e) {
        missingOrInvalidKeys.push(key);
      }
    }

    return missingOrInvalidKeys;
  }

  /**
   * Restaura chaves essenciais do localStorage que estão ausentes ou inválidas
   * @param keys Lista de chaves para restaurar
   */
  private restoreEssentialLocalStorageKeys(keys: string[]): void {
    for (const key of keys) {
      // Se temos um valor padrão para esta chave
      if (key in this.defaultLocalStorageValues) {
        try {
          localStorage.setItem(key, this.defaultLocalStorageValues[key]);
          console.log(`Restaurado valor padrão para ${key}`);
        } catch (e) {
          console.error(`Falha ao restaurar valor padrão para ${key}:`, e);
        }
      } else if (key === 'user') {
        // Caso especial: token do usuário - talvez precise redirecionar para login
        // console.warn(
        //   'Token de usuário ausente ou inválido - pode ser necessário reautenticação'
        // );
        // Opcionalmente: this.redirectToLogin();
      }
      // Adicione outros casos especiais conforme necessário
    }
  }

  /**
   * Tenta recuperar a aplicação limpando caches e storages
   */
  async attemptRecovery(customReason?: string): Promise<void> {
    // Evita múltiplas tentativas simultâneas
    if (this.recoveryAttempted) {
      console.log('Recuperação já está em andamento');
      return;
    }

    console.group('Processo de Recuperação');
    console.log('Tentativa de recuperação iniciada');
    const startTime = Date.now();

    // Verificar primeiro se devemos iniciar recuperação
    const reason = customReason || this.determineRecoveryReason();
    if (!this.recoveryManager.shouldInitiateRecovery(reason)) {
      console.log('Recuperação não iniciada devido a restrições');
      console.groupEnd();
      return;
    }

    this.recoveryAttempted = true;
    console.log(`Tentando recuperar aplicação: ${reason}`);

    // Definir etapas da recuperação para visualização
    const steps = [
      'Preparando sistema de recuperação...',
      'Limpando cache do navegador...',
      'Verificando dados essenciais...',
      'Restaurando configurações...',
      'Finalizando recuperação...'
    ];
    try {
      // Forçar redirecionamento para a tela de recuperação ANTES de iniciar o processo
      // Isso garante que o usuário veja a tela de recuperação imediatamente
      console.log('Redirecionando para tela de recuperação...');
      await this.router.navigate(['/recovery'], { skipLocationChange: true });

      // Pequeno delay para garantir que o redirecionamento aconteça
      await new Promise(resolve => setTimeout(resolve, 200));

      // Somente após o redirecionamento, iniciar o processo de recuperação visual
      console.log(
        'Redirecionamento concluído, iniciando processo de recuperação visual'
      );
      await this.recoveryManager.startRecovery(reason, '', steps);

      // 1. Limpar todos os caches (Etapa 1)
      this.recoveryManager.updateProgress(20, 1);
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));

      // 2. Backup e verificação de dados essenciais do localStorage (Etapa 2)
      this.recoveryManager.updateProgress(40, 2);
      const essentialData: Record<string, string> = {};
      const criticalKeys = this.cacheService.getCriticalLocalStorageKeys();

      // Verificar e salvar dados essenciais
      for (const key of criticalKeys) {
        try {
          const value = localStorage.getItem(key);
          if (value !== null) {
            essentialData[key] = value;
          } else if (key in this.defaultLocalStorageValues) {
            essentialData[key] = this.defaultLocalStorageValues[key];
          }
        } catch (e) {
          if (key in this.defaultLocalStorageValues) {
            essentialData[key] = this.defaultLocalStorageValues[key];
          }
        }
      }

      // 3. Limpar localStorage (Etapa 3)
      this.recoveryManager.updateProgress(50, 3);
      const credentials = localStorage.getItem(KEY_CREDENTIAIS);
      const URL_API = localStorage.getItem(TOKEN_KEYS.URL_API);
      localStorage.clear();
      if (credentials) localStorage.setItem(KEY_CREDENTIAIS, credentials);
      if (URL_API) localStorage.setItem(TOKEN_KEYS.URL_API, URL_API);

      // 4. Restaurar dados essenciais
      this.recoveryManager.updateProgress(60, 3);
      Object.keys(essentialData).forEach(key => {
        try {
          localStorage.setItem(key, essentialData[key]);
        } catch (e) {
          console.error(`Falha ao restaurar ${key}:`, e);
        }
      });

      // 5. Limpar sessionStorage
      this.recoveryManager.updateProgress(70, 4);
      sessionStorage.clear();

      // 6. Limpar IndexedDB se necessário
      this.recoveryManager.updateProgress(80, 4);
      await this.clearIndexedDB();

      // 7. Caso usuário ainda esteja logado remove o token
      if (this.tokenService.getToken()) {
        await lastValueFrom(
          this.tokenService.onLogout().pipe(
            defaultIfEmpty(false) // valor padrão se o Observable completar sem emitir
          )
        );
      }
      this.recoveryManager.updateProgress(90, 4);
      localStorage.clear();
      if (credentials) localStorage.setItem(KEY_CREDENTIAIS, credentials);
      if (URL_API) localStorage.setItem(TOKEN_KEYS.URL_API, URL_API);

      console.log('Recuperação concluída com sucesso');

      // 8. Registrar evento para análise posterior
      this.logRecoveryEvent(
        {
          keysRestored: Object.keys(essentialData),
          cacheCleared: cacheNames.length,
          cacheNames: cacheNames.join(', '),
          recoveryTime: Date.now() - startTime // tempo de recuperação em ms
        },
        reason
      );

      // Finalizar o processo visual (vai para 100%)
      this.recoveryManager.updateProgress(100, 5);

      // Pequeno delay para garantir que o usuário veja o progresso completo
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Finalizar recuperação
      this.recoveryManager.finishRecovery(true);
      this.stopMonitoring();
    } catch (error) {
      console.error('Erro durante tentativa de recuperação:', error);
      // Finalizar com erro
      this.recoveryManager.finishRecovery(false);
      // Registrar o erro no log de recuperação
      this.logRecoveryEvent(
        {
          error: error.toString(),
          stack: error.stack,
          recoveryTime: Date.now() - startTime
        },
        `Falha: ${reason}`
      );
    } finally {
      this.recoveryAttempted = false;
      console.log('Processo de recuperação finalizado');
      console.groupEnd();
    }
  }

  /**
   * Determina o motivo mais provável para a recuperação
   */
  private determineRecoveryReason(): string {
    if (
      this.appState && localStorage.getItem(KEY_APP_STATE) &&
      (!this.appState.isAppStable() || !this.appState.currentState)
    ) {
      return;
    }

    // Verificar localStorage
    let localStorageIssue = false;
    try {
      localStorage.setItem(KEY_RECOVERY_TEST, 'test');
      localStorage.removeItem(KEY_RECOVERY_TEST);
    } catch (e) {
      localStorageIssue = true;
    }

    // Verificar cache
    let cacheIssue = false;
    try {
      const testCache = window.caches;
      if (!testCache) {
        cacheIssue = true;
      }
    } catch (e) {
      cacheIssue = true;
    }

    // Verificar performance usando métodos seguros
    const performanceIssue = this.detectPerformanceIssues();

    // Verificar erros recentes no console
    const recentErrors = this.hasRecentUnhandledErrors;

    // Verificar tempos de resposta lentos
    const responseIssues = this.hasSlowResponses;

    // Determinar motivo principal
    if (cacheIssue) {
      return 'Problemas de Cache do Navegador';
    } else if (localStorageIssue) {
      return 'Problemas de Armazenamento Local';
    } else if (performanceIssue) {
      return 'Problemas de Performance';
    } else if (responseIssues) {
      return 'Tempos de Resposta Lentos';
    } else if (recentErrors) {
      return 'Erros Detectados na Aplicação';
    }
    // else {
    //   return 'Problemas Técnicos Detectados'; // Está relacionada a falta de alguma chave no localStorage ou algo do gênero
    // }
  }

  /**
   * Detecta problemas de performance de forma segura e compatível
   */
  private detectPerformanceIssues(): boolean {
    try {
      // Teste simples de performance JavaScript
      const startTime = Date.now();
      let counter = 0;

      // Loop pequeno para testar a velocidade de processamento
      for (let i = 0; i < 100000; i++) {
        counter += i;
      }

      const executionTime = Date.now() - startTime;

      // Se o loop simples demorar mais de 100ms, pode indicar sobrecarga
      const slowExecution = executionTime > 100;

      // Verificar outras métricas de performance
      let highLoadTime = false;

      // Usar navegação API se disponível
      if (window.performance && window.performance.timing) {
        const navTiming = window.performance.timing;
        const pageLoadTime = navTiming.loadEventEnd - navTiming.navigationStart;
        const domReadyTime = navTiming.domComplete - navTiming.domLoading;

        // Considerar página lenta se demorar mais de 5 segundos para carregar
        // ou DOM demorar mais de 3 segundos para ficar pronto
        highLoadTime = pageLoadTime > 5000 || domReadyTime > 3000;
      }

      // Retornar verdadeiro se qualquer indicador de problema de performance for detectado
      return slowExecution || highLoadTime;
    } catch (e) {
      console.error('Erro ao verificar performance:', e);
      return false;
    }
  }

  // Propriedades para rastrear estado do sistema
  private hasRecentUnhandledErrors = false;
  private hasSlowResponses = false;

  // Modificar o método que monitora requisições para detectar respostas lentas
  private monitorNetworkRequests(): void {
    // Intercepta requisições fetch
    const originalFetch = window.fetch;
    window.fetch = (input: RequestInfo, init?: RequestInit) => {
      const startTime = Date.now();

      return new Promise((resolve, reject) => {
        // Variável para controlar se a requisição já foi concluída
        let isRequestCompleted = false;

        // Criar um ID para o timeout para poder cancelá-lo depois
        let timeoutId: number;
        originalFetch(input, init)
          .then(response => {
            // Marcar como concluída
            isRequestCompleted = true;
            // Cancelar o timeout para evitar processamento desnecessário
            clearTimeout(timeoutId);

            const elapsedTime = Date.now() - startTime;

            // Se a resposta demorar muito, mas eventualmente chegar
            if (elapsedTime > this.requestTimeoutMs * 0.8) {
              // console.warn(
              //   `Requisição lenta (${elapsedTime}ms): ${input.toString()}`
              // );
              this.hasSlowResponses = true;

              // Resetar após 5 minutos
              setTimeout(() => {
                this.hasSlowResponses = false;
              }, 5 * 60 * 1000);
            }

            resolve(response);
          })
          .catch(error => {
            // Marcar como concluída
            isRequestCompleted = true;

            // Cancelar o timeout
            clearTimeout(timeoutId);
            if (this.isCacheRelatedError(error)) {
              this.attemptRecovery().then(() => {
                // Tenta novamente após recuperação
                originalFetch(input, init)
                  .then(resolve)
                  .catch(reject);
              });
            } else {
              reject(error);
            }
          });

        // Timeout para a requisição
        timeoutId = window.setTimeout(() => {
          // Verificar se a requisição já foi concluída para evitar processamento duplicado
          if (!isRequestCompleted) {
            console.error(`Timeout na requisição: ${input.toString()}`);
            this.hasSlowResponses = true;

            // Resetar após 5 minutos
            setTimeout(() => {
              this.hasSlowResponses = false;
            }, 5 * 60 * 1000);

            this.attemptRecovery().then(() => {
              // Tenta novamente após recuperação
              originalFetch(input, init)
                .then(response => {
                  if (!isRequestCompleted) {
                    isRequestCompleted = true;
                    resolve(response);
                  }
                })
                .catch(error => {
                  if (!isRequestCompleted) {
                    isRequestCompleted = true;
                    reject(error);
                  }
                });
            });
          }
        }, this.requestTimeoutMs);
      });
    };
  }

  /**
   * Limpa bancos de dados IndexedDB
   */
  private async clearIndexedDB(): Promise<void> {
    return new Promise(resolve => {
      const databases = indexedDB.databases
        ? indexedDB.databases()
        : Promise.resolve([]);

      databases
        .then(dbs => {
          const deletePromises = dbs.map(db => {
            if (db.name) {
              return new Promise<void>(deleteResolve => {
                const request = indexedDB.deleteDatabase(db.name!);
                request.onsuccess = () => deleteResolve();
                request.onerror = () => deleteResolve(); // Continue mesmo com erro
              });
            }
            return Promise.resolve();
          });

          Promise.all(deletePromises).then(() => resolve());
        })
        .catch(() => resolve()); // Continue mesmo se falhar ao listar bancos
    });
  }

  /**
   * Determina se é necessário recarregar a página
   */
  private shouldReloadPage(): boolean {
    // Lógica para determinar se a página precisa ser recarregada
    // Por exemplo, se a tentativa de recuperação foi devido a um erro grave
    return true; // Simplificado para este exemplo
  }

  /**
   * Registra evento de recuperação para análise posterior
   */
  private logRecoveryEvent(details: any = {}, reason: string = ''): void {
    const recoveryLog = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      reason: reason || this.determineRecoveryReason(),
      details,
      browserInfo: {
        language: navigator.language,
        platform: navigator.platform,
        cookiesEnabled: navigator.cookieEnabled
      }
    };

    // Armazenar log localmente (temporário)
    try {
      const logs = JSON.parse(localStorage.getItem(KEY_RECOVERY_LOG) || '[]');
      logs.push(recoveryLog);
      // Manter apenas os últimos 10 logs
      localStorage.setItem(KEY_RECOVERY_LOG, JSON.stringify(logs.slice(-10)));
    } catch (e) {
      // Ignorar erros de localStorage
      // console.warn(
      //   'Não foi possível salvar log de recuperação no localStorage'
      // );
    }

    // Opcionalmente, enviar log para servidor
    // this.sendLogToServer(recoveryLog);
    console.log('Log de recuperação registrado:', recoveryLog);
  }

  // Método para enviar logs para o servidor (implementação depende da sua API)
  private sendLogToServer(log: any): void {
    // Implementação para enviar logs para o servidor
  }
}
